import type { ProductPack } from '../types/packs';

type GtagCommand = 'event' | 'config' | 'set' | 'js' | 'consent';

interface GtagEventParams {
  [key: string]: string | number | boolean | undefined | null | object | Array<any>;
}

interface Gtag {
  (command: 'event', eventName: string, eventParams?: GtagEventParams): void;
  (command: 'config', targetId: string, configParams?: GtagEventParams): void;
  (command: 'set', params: GtagEventParams): void;
  (command: 'js', date: Date): void;
  (command: 'consent', action: 'default' | 'update', params: GtagEventParams): void;
}

declare var gtag: Gtag | undefined;
declare var dataLayer: Record<string, any>[] | undefined;
declare var fbq: ((command: string, event: string, params?: Record<string, any>) => void) | undefined;

/**
 * Async wrapper for gtag - defers execution to idle time to avoid blocking INP.
 * Same API as gtag, fire and forget.
 */
export function gtagAsync(command: 'event', eventName: string, eventParams?: GtagEventParams): void;
export function gtagAsync(command: 'config', targetId: string, configParams?: GtagEventParams): void;
export function gtagAsync(command: 'set', params: GtagEventParams): void;
export function gtagAsync(command: GtagCommand, ...rest: any[]): void {
  if (typeof gtag !== 'function') return;
  const g = gtag; // Capture reference for closure

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => (g as any)(command, ...rest), { timeout: 2000 });
  } else {
    setTimeout(() => (g as any)(command, ...rest), 0);
  }
}

/**
 * Async wrapper for dataLayer.push - defers execution to idle time.
 */
export function dataLayerPushAsync(data: Record<string, any>): void {
  if (typeof dataLayer === 'undefined') return;
  const dl = dataLayer; // Capture reference for closure

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => dl.push(data), { timeout: 2000 });
  } else {
    setTimeout(() => dl.push(data), 0);
  }
}

/**
 * Async wrapper for fbq - defers execution to idle time.
 */
export function fbqAsync(command: string, event: string, params?: Record<string, any>): void {
  if (typeof fbq !== 'function') return;
  const f = fbq; // Capture reference for closure

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => f(command, event, params), { timeout: 2000 });
  } else {
    setTimeout(() => f(command, event, params), 0);
  }
}

export interface DiscountEventData {
  coupon_code: string;
  discount_value: number;
  currency?: string;
}

export function trackDiscountApplied(data: DiscountEventData): void {
  const eventData = {
    coupon: data.coupon_code,
    discount_value: data.discount_value,
    currency: data.currency || 'ARS',
  };

  gtagAsync('event', 'apply_coupon', eventData);
  dataLayerPushAsync({ event: 'apply_coupon', ...eventData });
}

export function trackDiscountFailed(couponCode: string): void {
  const eventData = { coupon: couponCode };

  gtagAsync('event', 'coupon_failed', eventData);
  dataLayerPushAsync({ event: 'coupon_failed', ...eventData });
}

export function trackPackAdded(pack: ProductPack): void {
  const eventData = {
    pack_id: pack.id,
    pack_name: pack.title,
    pack_value: pack.calculated_price,
    items_count: pack.items.length,
    currency: 'ARS',
  };

  gtagAsync('event', 'add_pack', eventData);
  dataLayerPushAsync({ event: 'add_pack', ...eventData });
}

/**
 * Track view_item event for GA4 ecommerce.
 * Should be fired when a user views a product detail page.
 */
export function trackViewItem(item: {
  item_id: number;
  item_name: string;
  item_category?: string;
  price?: number;
}): void {
  const eventData = {
    currency: 'ARS',
    items: [item],
  };

  gtagAsync('event', 'view_item', eventData);
  dataLayerPushAsync({ ecommerce: null });
  dataLayerPushAsync({ event: 'view_item', ecommerce: eventData });
}

/**
 * Track first time purchase event.
 * Should be fired when a user completes their first order.
 */
export function trackFirstTimePurchase(data: {
  transaction_id: number;
  value: number;
  currency?: string;
}): void {
  const eventData = {
    transaction_id: data.transaction_id,
    value: data.value,
    currency: data.currency || 'ARS',
  };

  gtagAsync('event', 'first_time_purchase', eventData);
  dataLayerPushAsync({ event: 'first_time_purchase', ...eventData });
}

/**
 * Track product list views (category browsing).
 * Fires both GA4 and dataLayer events.
 */
export function trackViewProductList(listId: string, itemsCount: number): void {
  const eventData = { list_id: listId, items_count: itemsCount };

  gtagAsync('event', 'view_item_list', eventData);
  dataLayerPushAsync({ event: 'view_product_list', ...eventData });
}

/**
 * Track search events.
 * Fires both GA4 and dataLayer events.
 */
export function trackSearch(query: string, resultsCount: number): void {
  const eventData = { query, results_count: resultsCount };

  gtagAsync('event', 'search', { search_term: query, ...eventData });
  dataLayerPushAsync({ event: 'search', ...eventData });
}

/**
 * GA4 begin_checkout with the ecommerce payload (#95). Resets the ecommerce
 * object first, like every GA4 ecommerce push.
 */
export function trackBeginCheckout(ecommerce: Record<string, any>): void {
  dataLayerPushAsync({ ecommerce: null });
  dataLayerPushAsync({ event: 'begin_checkout', ecommerce });
}

/**
 * Track checkout start.
 * Fires only dataLayer (routed to PostHog via GTM).
 */
export function trackStartCheckout(cartValue: number, itemsCount: number): void {
  dataLayerPushAsync({
    event: 'start_checkout',
    cart_value: cartValue,
    items_count: itemsCount,
    currency: 'ARS',
  });
}

/**
 * Track when a checkout step becomes visible.
 */
export function trackCheckoutStepViewed(step: string, cartValue: number): void {
  dataLayerPushAsync({
    event: 'checkout_step_viewed',
    step,
    cart_value: cartValue,
    currency: 'ARS',
  });
}

/**
 * Track when a checkout step is completed.
 */
export function trackCheckoutStepCompleted(step: string, cartValue: number): void {
  dataLayerPushAsync({
    event: 'checkout_step_completed',
    step,
    cart_value: cartValue,
    currency: 'ARS',
  });
}

/**
 * Track signup button click on landing pages.
 * Uses synchronous push since the user navigates away to Auth0 immediately.
 */
export function trackSignupClicked(landing: string, position: string): void {
  const eventData = { event: 'signup_button_clicked', landing, position };

  if (typeof dataLayer !== 'undefined') {
    dataLayer.push(eventData);
  }
  if (typeof gtag === 'function') {
    gtag('event', 'signup_button_clicked', { landing, position });
  }
}

/**
 * Gift wizard funnel events. Each step fires both GA4 and dataLayer
 * so we can stitch the funnel in PostHog (via GTM) and GA4.
 */
type GiftStep = 'intro' | 'recipient' | 'from' | 'message' | 'delivery' | 'preview';

export function trackGiftStepViewed(step: GiftStep, packsSelected: number): void {
  const eventData = { step, packs_selected: packsSelected };
  gtagAsync('event', 'gift_step_viewed', eventData);
  dataLayerPushAsync({ event: 'gift_step_viewed', ...eventData });
}

export function trackGiftPackToggled(action: 'add' | 'remove', packId: number, packsSelected: number): void {
  const eventData = { action, pack_id: packId, packs_selected: packsSelected };
  gtagAsync('event', 'gift_pack_toggled', eventData);
  dataLayerPushAsync({ event: 'gift_pack_toggled', ...eventData });
}

export function trackGiftStepCompleted(step: GiftStep, packsSelected: number, totalValue: number): void {
  const eventData = { step, packs_selected: packsSelected, value: totalValue, currency: 'ARS' };
  gtagAsync('event', 'gift_step_completed', eventData);
  dataLayerPushAsync({ event: 'gift_step_completed', ...eventData });
}

export function trackGiftStepError(step: GiftStep, reason: string): void {
  const eventData = { step, reason };
  gtagAsync('event', 'gift_step_error', eventData);
  dataLayerPushAsync({ event: 'gift_step_error', ...eventData });
}

export function trackGiftConfirmed(packsSelected: number, totalValue: number): void {
  const eventData = { packs_selected: packsSelected, value: totalValue, currency: 'ARS' };
  gtagAsync('event', 'gift_confirmed', eventData);
  dataLayerPushAsync({ event: 'gift_confirmed', ...eventData });
}

/**
 * Track page views for SPA navigation (Inertia).
 * Called on route changes to ensure GA4 and Meta Pixel receive pageview events.
 */
export function trackPageView(url: string): void {
  gtagAsync('event', 'page_view', {
    page_path: url,
    page_title: document.title,
  });

  dataLayerPushAsync({
    event: 'pageview',
    page_path: url,
    page_title: document.title,
  });

  fbqAsync('track', 'PageView');
}
